import React from 'react';
import styled from 'styled-components';

const FormattedContent = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #1f2937;

  h3 {
    font-size: 15px;
    font-weight: 600;
    margin: 12px 0 8px 0;
    color: #111827;

    &:first-child {
      margin-top: 0;
    }
  }

  p {
    margin: 8px 0;
  }

  ul, ol {
    margin: 12px 0;
    padding-left: 24px;

    li {
      margin: 6px 0;
      line-height: 1.6;

      /* Add some visual separation between list items */
      &:not(:last-child) {
        padding-bottom: 4px;
      }
    }
  }

  /* Make numbered lists stand out more */
  ol {
    li {
      font-weight: 500;

      /* Make the list content normal weight */
      > * {
        font-weight: 400;
      }
    }
  }

  /* Highlight sections */
  .highlights {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 600;
    margin-bottom: 8px;
  }

  .lowlights {
    color: #dc2626;
    font-weight: 600;
    margin-bottom: 8px;
  }

  /* Key-value pairs */
  .key-value {
    display: flex;
    align-items: baseline;
    margin: 6px 0;

    .key {
      font-weight: 500;
      color: #4b5563;
      min-width: 140px;
      &:after {
        content: ':';
        margin-right: 8px;
      }
    }

    .value {
      color: #111827;
      font-weight: 400;

      &.good {
        color: #059669;
        font-weight: 500;
      }

      &.warning {
        color: #d97706;
        font-weight: 500;
      }

      &.bad {
        color: #dc2626;
        font-weight: 500;
      }
    }
  }

  /* Score badge */
  .score-badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 600;
    margin: 8px 0;

    &.pass {
      background: #d1fae5;
      color: #065f46;
    }

    &.fail {
      background: #fee2e2;
      color: #991b1b;
    }

    &.warning {
      background: #fed7aa;
      color: #92400e;
    }
  }

  /* Section divider */
  .section {
    margin: 16px 0;
    padding-top: 12px;
    border-top: 1px solid #e5e7eb;

    &:first-child {
      border-top: none;
      padding-top: 0;
      margin-top: 0;
    }
  }

  /* Emphasis text */
  strong {
    font-weight: 600;
    color: #111827;
  }

  em {
    font-style: italic;
    color: #4b5563;
  }

  /* Number highlights */
  .number {
    font-weight: 600;
    color: #4f46e5;
  }

  /* Alert box */
  .alert {
    padding: 12px;
    border-radius: 8px;
    margin: 12px 0;

    &.info {
      background: #eff6ff;
      border-left: 3px solid #3b82f6;
      color: #1e40af;
    }

    &.warning {
      background: #fef3c7;
      border-left: 3px solid #f59e0b;
      color: #78350f;
    }

    &.error {
      background: #fef2f2;
      border-left: 3px solid #ef4444;
      color: #7f1d1d;
    }

    &.success {
      background: #f0fdf4;
      border-left: 3px solid #10b981;
      color: #14532d;
    }
  }
`;

interface MessageFormatterProps {
  content: string;
}

export const MessageFormatter: React.FC<MessageFormatterProps> = ({ content }) => {
  const formatMessage = (text: string): React.ReactNode[] => {
    const elements: React.ReactNode[] = [];

    const lines = text.split('\n');
    let currentList: string[] = [];
    let listType: 'ul' | 'ol' | null = null;
    let currentSection: React.ReactNode[] = [];
    let lastFlushedListEndNumber = 0; // Track last number from flushed numbered list

    const flushList = () => {
      if (currentList.length > 0 && listType) {
        const ListComponent = listType;
        const uniqueKey = `list-${elements.length}-${currentSection.length}-${Date.now()}`;
        
        // For ordered lists, track where this list ends for continuity
        const listProps: any = {};
        if (listType === 'ol') {
          // If we're continuing from a previous flushed list, set start attribute
          if (lastFlushedListEndNumber > 0) {
            const startNum = lastFlushedListEndNumber + 1;
            listProps.start = startNum;
            // Update tracking: this list starts at startNum and has currentList.length items
            lastFlushedListEndNumber = startNum + currentList.length - 1;
          } else {
            // First list or after reset - track where it ends
            lastFlushedListEndNumber = currentList.length;
          }
        }
        
        currentSection.push(
          <ListComponent key={uniqueKey} {...listProps}>
            {currentList.map((item, idx) => (
              <li key={idx}>{parseInlineFormatting(item)}</li>
            ))}
          </ListComponent>
        );
        currentList = [];
        listType = null;
      }
    };

    const parseInlineFormatting = (text: string): React.ReactNode => {
      // Parse for monetary values and make them stand out
      text = text.replace(/\$[\d,]+(\.\d{2})?/g, '<span class="number">$&</span>');

      // Parse for percentages
      text = text.replace(/\d+(\.\d+)?%/g, '<span class="number">$&</span>');

      // Parse for ratios
      text = text.replace(/\d+(\.\d+)?:\d+(\.\d+)?/g, '<span class="number">$&</span>');

      // Parse for scores
      text = text.replace(/\b\d{3}\b/g, '<span class="number">$&</span>');

      // Parse bold text (using ** or __)
      text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      text = text.replace(/__(.*?)__/g, '<strong>$1</strong>');

      // Parse italic text (using * or _)
      text = text.replace(/\*(.*?)\*/g, '<em>$1</em>');
      text = text.replace(/_(.*?)_/g, '<em>$1</em>');

      return <span dangerouslySetInnerHTML={{ __html: text }} />;
    };

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      // Skip empty lines
      if (!trimmedLine) {
        flushList();
        // Don't create new section on every empty line - keep building current section
        // This preserves list continuity across empty lines
        return;
      }

      // Check for headers
      // 1. Lines ending with : (but not list items or key-value pairs)
      // 2. All-caps lines (section headers like CRITICAL RISKS)
      const isHeaderWithColon = trimmedLine.endsWith(':') &&
          trimmedLine.length < 50 &&
          !trimmedLine.match(/^\d+[\.)]\s+/) && // Not a numbered list item
          !trimmedLine.match(/^[-•*]\s+/) && // Not a bullet point
          !trimmedLine.includes(': '); // Not a key-value pair (colon in middle)

      const isAllCapsHeader = trimmedLine === trimmedLine.toUpperCase() &&
          trimmedLine.length > 3 && // Minimum length
          trimmedLine.length < 50 && // Maximum length
          /^[A-Z\s\-:]+$/.test(trimmedLine) && // Only caps, spaces, hyphens, colons
          !trimmedLine.match(/^\d+[\.)]\s+/) && // Not a numbered list
          !trimmedLine.match(/^[-•*]\s+/); // Not a bullet point

      if (isHeaderWithColon || isAllCapsHeader) {
        flushList();
        lastFlushedListEndNumber = 0; // Reset list continuity at headers
        const headerText = isHeaderWithColon ? trimmedLine.slice(0, -1) : trimmedLine;
        currentSection.push(
          <h3 key={`header-${index}`}>
            {headerText}
          </h3>
        );
        return;
      }

      // Check for bullet points and numbered lists FIRST (before other content checks)
      // This ensures list items containing keywords like "FAIL" aren't misidentified

      // Check for bullet points
      if (trimmedLine.match(/^[-•*]\s+/)) {
        const content = trimmedLine.replace(/^[-•*]\s+/, '');
        if (listType !== 'ul') {
          flushList();
          listType = 'ul';
        }
        currentList.push(content);
        return;
      }

      // Check for numbered lists
      const numberedMatch = trimmedLine.match(/^(\d+)[\.)]\s+/);
      if (numberedMatch) {
        const currentNumber = parseInt(numberedMatch[1]);
        const content = trimmedLine.replace(/^\d+[\.)]\s+/, '');

        // Heuristic: Check if this looks like an embedded number in a sentence
        // rather than a real list item
        const looksLikeEmbeddedNumber =
          // Has multiple numbered items in one line with commas
          /\d+[\.)]\s+.+,\s*\d+[\.)]\s+/.test(trimmedLine) ||
          // Very short content after number (< 15 chars) with "and" nearby
          (content.length < 15 && /\band\b/.test(trimmedLine));

        if (looksLikeEmbeddedNumber) {
          // Treat as regular paragraph, not a list
          flushList();
          currentSection.push(
            <p key={`p-${index}`}>
              {parseInlineFormatting(trimmedLine)}
            </p>
          );
          return;
        }

        // Heuristic: Check if this looks like a section header formatted as numbered item
        // LLMs sometimes return "1. SECTION NAME" when they mean a sub-header
        const looksLikeSectionHeader =
          content.length < 40 && // Short text
          content === content.toUpperCase() && // ALL CAPS
          /^[A-Z\s\-:]+$/.test(content) && // Only letters, spaces, hyphens, colons
          currentNumber === 1; // Starts at 1 (section markers usually do)

        if (looksLikeSectionHeader) {
          // Treat as a sub-heading, not a list item
          flushList();
          currentSection.push(
            <h3 key={`header-${index}`}>
              {content}
            </h3>
          );
          return;
        }

        // SIMPLE LOGIC: 
        // - If no current list, start one
        // - If switching from bullets to numbers, flush bullets and start numbers
        // - If we see "1" and already have items, it's a new list (flush)
        // - Otherwise, continue the current numbered list
        
        if (listType === null) {
          // Start new numbered list
          // Check if this continues from a previously flushed list
          if (currentNumber !== lastFlushedListEndNumber + 1 && currentNumber === 1) {
            // Starting fresh at 1 - reset continuity
            lastFlushedListEndNumber = 0;
          }
          listType = 'ol';
        } else if (listType === 'ul') {
          // Switching from bullets to numbers - always flush bullets
          flushList();
          listType = 'ol';
        } else if (listType === 'ol') {
          // Already in numbered list
          if (currentNumber === 1 && currentList.length > 0) {
            // New list starting at 1 - flush previous and reset continuity
            flushList();
            lastFlushedListEndNumber = 0; // Reset - this is a new list context
            listType = 'ol';
          }
          // else: continue building current list (items 2, 3, 4, etc.)
        }

        currentList.push(content);
        return;
      }

      // Check for highlights/lowlights (AFTER list checks to avoid false positives)
      if (trimmedLine.toLowerCase().includes('highlights:')) {
        flushList();
        currentSection.push(
          <div key={`highlights-${index}`} className="highlights">
            {trimmedLine}
          </div>
        );
        return;
      }

      if (trimmedLine.toLowerCase().includes('lowlights:') ||
          trimmedLine.toLowerCase().includes('red flag')) {
        flushList();
        currentSection.push(
          <div key={`lowlights-${index}`} className="lowlights">
            {trimmedLine}
          </div>
        );
        return;
      }

      // Check for score badges (AFTER list checks)
      if (trimmedLine.includes('PASS') || trimmedLine.includes('FAIL') ||
          trimmedLine.includes('CONDITIONAL')) {
        flushList();
        let badgeClass = 'warning';
        if (trimmedLine.includes('PASS')) badgeClass = 'pass';
        if (trimmedLine.includes('FAIL')) badgeClass = 'fail';

        currentSection.push(
          <div key={`score-${index}`} className={`score-badge ${badgeClass}`}>
            {trimmedLine}
          </div>
        );
        return;
      }

      // Check for key-value pairs (text with : in the middle)
      // MUST come after numbered list check to avoid catching "3. Item: value"
      if (trimmedLine.includes(':') && !trimmedLine.endsWith(':')) {
        const colonIndex = trimmedLine.indexOf(':');
        const key = trimmedLine.substring(0, colonIndex);
        const value = trimmedLine.substring(colonIndex + 1).trim();

        // Only treat as key-value if key looks like a label:
        // - Short (< 30 chars)
        // - No commas (not a sentence fragment)
        // - NOT a numbered or bulleted list item
        // - Has actual content after colon
        const looksLikeKeyValue = key.length < 30 &&
                                  !key.includes(',') &&
                                  !key.match(/^\d+[\.)]\s+/) && // Not numbered list
                                  !key.match(/^[-•*]\s+/) && // Not bullet list
                                  value.length > 0 &&
                                  value.length < 200; // Value shouldn't be super long

        if (looksLikeKeyValue) {
          flushList();

          // Determine value class based on content
          let valueClass = '';
          if (value.toLowerCase().includes('good') ||
              value.toLowerCase().includes('excellent') ||
              value.toLowerCase().includes('pass')) {
            valueClass = 'good';
          } else if (value.toLowerCase().includes('warning') ||
                     value.toLowerCase().includes('conditional')) {
            valueClass = 'warning';
          } else if (value.toLowerCase().includes('fail') ||
                     value.toLowerCase().includes('bad') ||
                     value.toLowerCase().includes('low')) {
            valueClass = 'bad';
          }

          currentSection.push(
            <div key={`kv-${index}`} className="key-value">
              <span className="key">{key}</span>
              <span className={`value ${valueClass}`}>
                {parseInlineFormatting(value)}
              </span>
            </div>
          );
          return;
        }
      }

      // Check for important alerts
      if (trimmedLine.toLowerCase().includes('important') ||
          trimmedLine.toLowerCase().includes('note:') ||
          trimmedLine.toLowerCase().includes('warning:')) {
        flushList();
        let alertClass = 'info';
        if (trimmedLine.toLowerCase().includes('warning')) alertClass = 'warning';
        if (trimmedLine.toLowerCase().includes('error')) alertClass = 'error';

        currentSection.push(
          <div key={`alert-${index}`} className={`alert ${alertClass}`}>
            {parseInlineFormatting(trimmedLine)}
          </div>
        );
        return;
      }

      // Default paragraph
      flushList();
      currentSection.push(
        <p key={`p-${index}`}>
          {parseInlineFormatting(trimmedLine)}
        </p>
      );
    });

    // Flush any remaining list or section
    flushList();
    if (currentSection.length > 0) {
      elements.push(
        <div key={`section-${elements.length}`} className="section">
          {currentSection}
        </div>
      );
    }

    return elements;
  };

  return (
    <FormattedContent>
      {formatMessage(content)}
    </FormattedContent>
  );
};
